'use client';

import { useCallback, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { TeamData } from '../Cteamcard';
import type {
  CarouselTeamSelectInput,
  CteamscarouselRef,
} from '../Cteamscarousel';
import type { TeamInspectInput } from './Chero.types';

interface UseHeroCarouselControllerInput {
  isTeamsSettled: boolean;
  markTeamsSettled: () => void;
  onTeamDeploy?: (teamId: string) => void;
  onTeamInspect?: (input: TeamInspectInput) => void;
}

interface UseHeroCarouselControllerResult {
  carouselRef: RefObject<CteamscarouselRef | null>;
  handleCenteredTeamChange: (team: TeamData | null) => void;
  handleDeploy: () => void;
  handleInspect: () => void;
  handleTeamSelect: (input: CarouselTeamSelectInput) => void;
  handlePrevious: () => void;
  handleNext: () => void;
}

interface ShouldMarkTeamsSettledInput {
  isTeamsSettled: boolean;
  nextCenteredTeamId: string | null;
}

function shouldMarkTeamsSettled({
  isTeamsSettled,
  nextCenteredTeamId,
}: ShouldMarkTeamsSettledInput): boolean {
  return !isTeamsSettled && nextCenteredTeamId != null;
}

export function useHeroCarouselController({
  isTeamsSettled,
  markTeamsSettled,
  onTeamDeploy,
  onTeamInspect,
}: UseHeroCarouselControllerInput): UseHeroCarouselControllerResult {
  const carouselRef = useRef<CteamscarouselRef>(null);
  const [centeredTeamId, setCenteredTeamId] = useState<string | null>(null);

  const handleCenteredTeamChange = useCallback(
    (team: TeamData | null) => {
      const nextCenteredTeamId = team?.id ?? null;
      setCenteredTeamId(nextCenteredTeamId);

      if (shouldMarkTeamsSettled({ isTeamsSettled, nextCenteredTeamId })) {
        markTeamsSettled();
      }
    },
    [isTeamsSettled, markTeamsSettled],
  );

  const handleDeploy = useCallback(() => {
    if (!centeredTeamId) {
      return;
    }

    onTeamDeploy?.(centeredTeamId);
  }, [centeredTeamId, onTeamDeploy]);

  const handleInspect = useCallback(() => {
    if (!centeredTeamId) {
      return;
    }

    // The Inspect control is not the card itself — no source rect, centre grow.
    onTeamInspect?.({ teamId: centeredTeamId, morphSource: null });
  }, [centeredTeamId, onTeamInspect]);

  const handleTeamSelect = useCallback(
    ({ team, morphSource }: CarouselTeamSelectInput) => {
      onTeamInspect?.({ teamId: team.id, morphSource });
    },
    [onTeamInspect],
  );

  const handlePrevious = useCallback(() => {
    carouselRef.current?.scrollToPrevious();
  }, []);

  const handleNext = useCallback(() => {
    carouselRef.current?.scrollToNext();
  }, []);

  return {
    carouselRef,
    handleCenteredTeamChange,
    handleDeploy,
    handleInspect,
    handleTeamSelect,
    handlePrevious,
    handleNext,
  };
}
